import React, { useState, useCallback } from 'react';
import { Button, TextField, Grid, Typography, Box } from '@mui/material';
import { useTranslation } from '@/core/i18n/i18n';
import { Add } from '@mui/icons-material';
import { Focus } from '@shared/schema';

interface NewObjectiveProps {
  focus: Focus;
  handleAddObjective: (focusId: number, objective: string) => void;
  readOnlyMode: boolean | null;
  isAnyLoading: boolean;
}

const NewObjective: React.FC<NewObjectiveProps> = ({
  focus,
  handleAddObjective,
  readOnlyMode,
  isAnyLoading,
}) => {
  const [newObjective, setNewObjective] = useState<{ [key: number]: string }>({});
  const [fieldErrors, setFieldErrors] = useState<{ [key: number]: string }>({});
  const MAX_CHARACTERS = 255;

  const { t } = useTranslation();

  const setFocusError = useCallback((focusId: number, message?: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) {
        next[focusId] = message;
      } else {
        delete next[focusId];
      }
      return next;
    });
  }, []);

  const currentValue = newObjective[focus.APFocusId] || '';
  const characterCount = currentValue.length;
  const remainingCharacters = MAX_CHARACTERS - characterCount;
  const hasLengthError = Boolean(fieldErrors[focus.APFocusId]);
  const isAtLimit = characterCount >= MAX_CHARACTERS;

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      <Grid size="grow">
        <Box>
          <TextField
            fullWidth
            required
            placeholder={t('defineObjectives.writeObjective')}
            value={currentValue}
            onChange={({ target }) => {
              const { value } = target;
              if (value.length > MAX_CHARACTERS) {
                const nextValue = value.slice(0, MAX_CHARACTERS);
                setNewObjective((prev) => ({
                  ...prev,
                  [focus.APFocusId]: nextValue,
                }));
                setFocusError(
                  focus.APFocusId,
                  t('defineObjectives.maxLengthExceeded', { max: MAX_CHARACTERS.toString() }),
                );
                return;
              }
              setNewObjective((prev) => ({
                ...prev,
                [focus.APFocusId]: value,
              }));
              setFocusError(focus.APFocusId);
            }}
            onPaste={(event) => {
              const pastedText = event.clipboardData?.getData('text') ?? '';
              if (!pastedText) {
                return;
              }
              event.preventDefault();
              const input = event.target as HTMLInputElement;
              const selectionStart = input.selectionStart ?? currentValue.length;
              const selectionEnd = input.selectionEnd ?? currentValue.length;
              const selectionLength = Math.max(selectionEnd - selectionStart, 0);
              const available = MAX_CHARACTERS - (currentValue.length - selectionLength);
              if (available <= 0) {
                setFocusError(
                  focus.APFocusId,
                  t('defineObjectives.maxLengthExceeded', { max: MAX_CHARACTERS.toString() }),
                );
                return;
              }
              const nextValue =
                currentValue.slice(0, selectionStart) +
                pastedText.slice(0, available) +
                currentValue.slice(selectionEnd);
              setNewObjective((prev) => ({
                ...prev,
                [focus.APFocusId]: nextValue,
              }));
              if (available < pastedText.length) {
                setFocusError(
                  focus.APFocusId,
                  t('defineObjectives.maxLengthExceeded', { max: MAX_CHARACTERS.toString() }),
                );
              } else {
                setFocusError(focus.APFocusId);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                const value = newObjective[focus.APFocusId] ?? '';
                if (fieldErrors[focus.APFocusId]) {
                  e.preventDefault();
                  return;
                }
                if (value.trim() && value.length <= MAX_CHARACTERS) {
                  handleAddObjective(focus.APFocusId, value);
                  setNewObjective((prev) => ({
                    ...prev,
                    [focus.APFocusId]: '',
                  }));
                  setFocusError(focus.APFocusId);
                }
              }
            }}
            error={hasLengthError}
            helperText={fieldErrors[focus.APFocusId] ?? ' '}
            sx={{
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasLengthError ? '#d32f2f' : '#A5B2BB',
                },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.8rem',
              float: 'left',
              mt: 0.5,
              fontWeight: 500,
              color: hasLengthError || isAtLimit ? '#d32f2f' : '#555',
            }}
          >
            <strong>{t('defineActivities.maxCharacters')}.</strong>{' '}
            {t('defineActivities.remainingCharacters')}
            <strong style={{ color: hasLengthError || isAtLimit ? '#d32f2f' : '#00BCE4' }}>
              {remainingCharacters}
            </strong>
          </Typography>
        </Box>
      </Grid>
      <Grid size="auto">
        {!readOnlyMode && (
          <Button
            variant="contained"
            disabled={isAnyLoading || hasLengthError}
            disableElevation
            onClick={() => {
              const value = newObjective[focus.APFocusId] ?? '';
              if (fieldErrors[focus.APFocusId]) return;
              if (value.trim() && value.length <= MAX_CHARACTERS) {
                handleAddObjective(focus.APFocusId, value);
                setNewObjective((prev) => ({
                  ...prev,
                  [focus.APFocusId]: '',
                }));
                setFocusError(focus.APFocusId);
              }
            }}
            sx={{
              minWidth: 55,
              width: 55,
              height: 55,
              p: 0,
              border: '1px solid #A5B2BB',
              boxShadow: 'none',
              bgcolor: '#00BCE4',
            }}
          >
            <Add />
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default NewObjective;
